// BOMModal.ts — só o que a casca (_casca.ts) não tem.
//
// O resto (ModalOverlay, ModalContent, FormSection, Input, Select, MiniTable,
// AlertMessage, InfoBadge…) vem de `_casca`. Aqui ficam as três coisas que só
// a Ficha Técnica precisa: a escolha SKU-ou-Ficha de cada componente, a linha
// de formulário que monta um componente, e o campo CALCULADO — o que a tela
// mostra mas o usuário não digita.
//
// Tokens: só var(--u), var(--fs-*) e var(--sp-*).
import styled from "styled-components";

/**
 * A escolha "este componente é um SKU" vs. "este componente é OUTRA FICHA".
 *
 * É um radio, não um checkbox nem dois campos livres, porque multinível admite
 * exatamente UMA origem por linha: uma massa pode ser insumo comprado ou uma
 * ficha própria, nunca as duas. O componente desabilitado continua VISÍVEL (só
 * apagado) para o usuário enxergar a alternativa que não escolheu.
 */
export const EscolhaDeOrigem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-20);
  padding: var(--sp-8) 0;
  flex-wrap: wrap;
`;

export const OpcaoDeOrigem = styled.label`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
`;

export const RadioDeOrigem = styled.input`
  width: var(--sp-16);
  height: var(--sp-16);
  cursor: pointer;
  accent-color: ${(props) => props.theme.colors.selection};
`;

/**
 * A linha de formulário que monta UM componente antes de entrar na lista.
 *
 * Grid próprio em vez do FormGrid da casca: o FormGrid usa
 * `minmax(calc(var(--u) * 250), 1fr)` e quebraria quantidade, unidade e % de
 * perda em três linhas dentro de um bloco que já é estreito.
 */
export const LinhaDeComponente = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 160), 1fr));
  gap: var(--sp-12);
  align-items: flex-end;
`;

/**
 * Campo SOMENTE LEITURA de valor derivado.
 *
 * Visualmente diferente de um input desabilitado de propósito: input cinza diz
 * "você não pode editar agora"; isto aqui diz "isto não é seu para preencher".
 * O custo da ficha vem do custo dos insumos, que vem de movimento de estoque —
 * módulo que não existe. Por isso o conteúdo é um travessão, nunca um número.
 */
export const CampoCalculado = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-12);
  padding: var(--sp-10) var(--sp-12);
  border-radius: 8px;
  border: 1px dashed
    ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  background: #f4f7f9;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
`;

export const ValorCalculado = styled.span`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: #3d4d5c;
`;

/** Texto de apoio embaixo de um campo. Explica a regra, não repete o rótulo. */
export const Dica = styled.p`
  margin: var(--sp-4) 0 0 0;
  font-size: var(--fs-xs);
  line-height: 1.5;
  color: #5b6b7a;
`;

/** A lista de componentes ainda vazia. Estado normal, não erro. */
export const ListaVazia = styled.div`
  padding: var(--sp-16);
  text-align: center;
  font-size: var(--fs-sm);
  color: #5b6b7a;
  border: 1px dashed ${(props) => props.theme.colors.borderColor || "#e0e0e0"};
  border-radius: 8px;
`;

/** Cabeçalho do bloco de componentes: título à esquerda, contador à direita. */
export const CabecalhoDaLista = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-12);
  margin-bottom: var(--sp-12);
  flex-wrap: wrap;
`;
